import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import './styles.css';

import { Grid, Popover } from '@mui/material';
import { HSpacer, RoutalPalette, RoutalSelect, RoutalSelectMultiSelectOption } from '@nx-smartmonkey/ui';
import { isSameDay } from 'date-fns';
import * as dateFnsLocales from 'date-fns/locale';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { CalendarProps, DateRange, DefinedRange, DefinedRangeProps, Range, RangeKeyDict } from 'react-date-range';

import { Language } from '@nx-smartmonkey/shared/domain';
import { intl } from '../../helpers/IntGlobalProvider';
import { useRetrieveUser } from '../../hooks/user/useRetrieveUser';
import Button from '../Button';
import Text from '../Text';

export interface CursorPosProps {
  top: number;
  left: number;
}

type DateRangePickerProps = {
  id?: string;
  startDate?: Date;
  endDate?: Date;
  popoverPosition?: {
    top: number;
    left: number;
  };
  onClose: (...args: any[]) => any;
  onUpdate: (...args: any[]) => any;
};

export const DateRangePicker = ({
  id,
  popoverPosition,
  startDate,
  endDate,
  onClose,
  onUpdate,
}: DateRangePickerProps) => {
  const [range, setRange] = useState<Range>({
    startDate,
    endDate,
  });
  const { data: user } = useRetrieveUser();
  const language = user?.language.value ?? Language.getDefaultLanguage().value;

  useEffect(() => {
    setRange({
      ...range,
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const renderMonthAndYear = (
    currFocusedDate: Date,
    changeShownDate: (value: Date | number | string, mode?: `set` | `setYear` | `setMonth` | `monthOffset`) => void,
    props: CalendarProps
  ) => {
    const { showMonthArrow, locale: calendarLocale, minDate, maxDate, showMonthAndYearPickers } = props;

    const defaultMaxDate = moment(new Date()).add(`1`, `month`).toDate();
    const defaultMinDate = moment(new Date()).subtract(`3`, `year`).toDate();

    const upperYearLimit = (maxDate || defaultMaxDate).getFullYear();
    const lowerYearLimit = (minDate || defaultMinDate).getFullYear();

    const currentYear = new Date().getFullYear();
    const isCurrentYear = currentYear === currFocusedDate.getFullYear();

    const upperMonthLimit = (maxDate || defaultMaxDate).getMonth();

    return (
      <div onMouseUp={(e) => e.stopPropagation()} className="rdrMonthAndYearWrapper">
        {showMonthArrow ? (
          <button
            type="button"
            className="rdrNextPrevButton rdrPrevButton"
            onClick={() => changeShownDate(-1, `monthOffset`)}
          >
            <i />
          </button>
        ) : null}

        {showMonthAndYearPickers ? (
          <span className="rdrMonthAndYearPickers">
            <span className="rdrMonthPicker">
              <RoutalSelect
                id={`${id}-month-picker`}
                hideArrow
                popoverProps={{
                  maxHeight: `300px`,
                  options: [
                    ...Array.from(
                      { length: isCurrentYear ? upperMonthLimit : 12 },
                      (_: any, index: number): RoutalSelectMultiSelectOption => {
                        return {
                          key: `option-${index}`,
                          listItemStyles: {
                            whiteSpace: `normal` as any,
                            overflow: `auto`,
                            fontSize: `12px`,
                            width: `296px`,
                          },
                          onClick: () => {
                            changeShownDate(index, `setMonth`);
                          },
                          content: <Text style={{ cursor: `pointer` }}>{calendarLocale!.localize!.month(index)}</Text>,
                        };
                      }
                    ),
                  ],
                }}
              >
                <Grid container direction="row" wrap="nowrap">
                  <Grid item style={{ width: `100%` }}>
                    <Text
                      isBold
                      style={{
                        cursor: `pointer`,
                        color: RoutalPalette.button.primary.defaultColor,
                        textTransform: `capitalize`,
                      }}
                    >
                      {calendarLocale!.localize!.month(currFocusedDate.getMonth())}
                    </Text>
                  </Grid>

                  <Grid item>
                    <i
                      style={{
                        display: `block`,
                        width: 0,
                        height: 0,
                        padding: 0,
                        textAlign: `center`,
                        borderStyle: `solid`,
                        margin: `auto`,
                        borderWidth: `6px`,
                        borderColor: `transparent transparent transparent ${RoutalPalette.button.primary.defaultColor}`,
                        transform: `rotate(90deg)`,
                        marginTop: `6px`,
                      }}
                    />
                  </Grid>
                </Grid>
              </RoutalSelect>
            </span>

            <span className="rdrYearPicker">
              <RoutalSelect
                id={`${id}-year-picker`}
                popoverProps={{
                  maxHeight: `300px`,
                  options: [
                    ...new Array(upperYearLimit - lowerYearLimit + 1)
                      .fill(upperYearLimit)
                      .map((val, i): RoutalSelectMultiSelectOption => {
                        const year = val - i;
                        return {
                          key: `option-${year}`,
                          listItemStyles: {
                            whiteSpace: `normal` as any,
                            overflow: `auto`,
                            fontSize: `12px`,
                            width: `296px`,
                          },
                          onClick: () => {
                            changeShownDate(year, `setYear`);
                          },
                          content: <Text style={{ cursor: `pointer` }}>{year}</Text>,
                        };
                      }),
                  ],
                }}
                hideArrow
              >
                <Grid container direction="row" wrap="nowrap">
                  <Grid item style={{ width: `100%` }}>
                    <Text
                      isBold
                      style={{
                        cursor: `pointer`,
                        color: RoutalPalette.button.primary.defaultColor,
                      }}
                    >
                      {currFocusedDate.getFullYear()}
                    </Text>
                  </Grid>

                  <Grid item>
                    <i
                      style={{
                        display: `block`,
                        width: 0,
                        height: 0,
                        padding: 0,
                        textAlign: `center`,
                        borderStyle: `solid`,
                        margin: `auto`,
                        borderWidth: `6px`,
                        borderColor: `transparent transparent transparent ${RoutalPalette.button.primary.defaultColor}`,
                        transform: `rotate(90deg)`,
                        marginTop: `6px`,
                      }}
                    />
                  </Grid>
                </Grid>
              </RoutalSelect>
            </span>
          </span>
        ) : null}

        {showMonthArrow ? (
          <button
            type="button"
            className="rdrNextPrevButton rdrNextButton"
            onClick={() => changeShownDate(+1, `monthOffset`)}
          >
            <i />
          </button>
        ) : null}
      </div>
    );
  };

  return (
    <Popover
      slotProps={{
        paper: {
          variant: `outlined`,
          style: {
            border: `1px solid ${RoutalPalette.neutral10}`,
            backgroundColor: `white`,
            color: `${RoutalPalette.secondary00}`,
            boxShadow: `none`,
          },
        },
      }}
      anchorReference="anchorPosition"
      anchorPosition={{
        top: popoverPosition?.top ?? 0,
        left: popoverPosition?.left ?? 0,
      }}
      keepMounted
      open={Boolean(popoverPosition)}
      onClose={(event: any) => {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }}
    >
      <Grid container direction="row" alignItems="flex-start" justifyContent="center" wrap="nowrap">
        <DefinedRange
          ranges={[range]}
          rangeColors={[`#406867`]}
          staticRanges={[
            {
              label: intl.formatMessage({ id: `date_range_picker.this_week` }),
              range: (props?: DefinedRangeProps) => {
                const weekStartDate = moment(new Date()).locale(language).startOf(`week`);
                const weekEndDate = moment(new Date()).locale(language).endOf(`week`);

                return {
                  startDate: weekStartDate.toDate(),
                  endDate: weekEndDate.toDate(),
                };
              },
              isSelected: (selectionRange: Range): boolean => {
                const definedRange = {
                  startDate: moment(new Date()).locale(language).startOf(`week`),
                  endDate: moment(new Date()).locale(language).endOf(`week`),
                };

                if (!selectionRange.startDate || !selectionRange.endDate) {
                  return false;
                }

                return (
                  isSameDay(selectionRange.startDate, definedRange.startDate.toDate()) &&
                  isSameDay(selectionRange.endDate, definedRange.endDate.toDate())
                );
              },
            },
            {
              label: intl.formatMessage({ id: `date_range_picker.last_week` }),
              range: (props?: DefinedRangeProps) => {
                const weekStartDate = moment(new Date()).locale(language).subtract(`1`, `week`).startOf(`week`);
                const weekEndDate = moment(new Date()).locale(language).subtract(`1`, `week`).endOf(`week`);

                return {
                  startDate: weekStartDate.toDate(),
                  endDate: weekEndDate.toDate(),
                };
              },
              isSelected: (selectionRange: Range): boolean => {
                const definedRange = {
                  startDate: moment(new Date()).locale(language).subtract(`1`, `week`).startOf(`week`).toDate(),
                  endDate: moment(new Date()).locale(language).subtract(`1`, `week`).endOf(`week`).toDate(),
                };

                if (!selectionRange.startDate || !selectionRange.endDate) {
                  return false;
                }

                return (
                  isSameDay(selectionRange.startDate, definedRange.startDate) &&
                  isSameDay(selectionRange.endDate, definedRange.endDate)
                );
              },
            },
            {
              label: intl.formatMessage({ id: `date_range_picker.this_month` }),
              range: (props?: DefinedRangeProps) => ({
                startDate: moment(new Date()).locale(language).startOf(`month`).toDate(),
                endDate: new Date(),
              }),
              isSelected: (selectionRange: Range) => {
                const definedRange = {
                  startDate: moment(new Date()).locale(language).startOf(`month`).toDate(),
                  endDate: new Date(),
                };
                if (!selectionRange.startDate || !selectionRange.endDate) {
                  return false;
                }

                return (
                  isSameDay(selectionRange.startDate, definedRange.startDate) &&
                  isSameDay(selectionRange.endDate, definedRange.endDate)
                );
              },
            },
            {
              label: intl.formatMessage({ id: `date_range_picker.last_month` }),
              range: (props?: DefinedRangeProps) => ({
                startDate: moment(new Date()).locale(language).subtract(`1`, `month`).startOf(`month`).toDate(),
                endDate: moment(new Date()).locale(language).subtract(`1`, `month`).endOf(`month`).toDate(),
              }),
              isSelected: (selectionRange: Range) => {
                const definedRange = {
                  startDate: moment(new Date()).locale(language).subtract(`1`, `month`).startOf(`month`).toDate(),
                  endDate: moment(new Date()).locale(language).subtract(`1`, `month`).endOf(`month`).toDate(),
                };
                if (!selectionRange.startDate || !selectionRange.endDate) {
                  return false;
                }

                return (
                  isSameDay(selectionRange.startDate, definedRange.startDate) &&
                  isSameDay(selectionRange.endDate, definedRange.endDate)
                );
              },
            },
            {
              label: intl.formatMessage({ id: `date_range_picker.this_quarter` }),
              range: (props?: DefinedRangeProps) => ({
                startDate: moment(new Date()).locale(language).startOf(`quarter`).toDate(),
                endDate: new Date(),
              }),
              isSelected: (selectionRange: Range) => {
                const definedRange = {
                  startDate: moment(new Date()).locale(language).startOf(`quarter`).toDate(),
                  endDate: new Date(),
                };
                if (!selectionRange.startDate || !selectionRange.endDate) {
                  return false;
                }

                return (
                  isSameDay(selectionRange.startDate, definedRange.startDate) &&
                  isSameDay(selectionRange.endDate, definedRange.endDate)
                );
              },
            },
            {
              label: intl.formatMessage({ id: `date_range_picker.last_quarter` }),
              range: (props?: DefinedRangeProps) => ({
                startDate: moment(new Date()).locale(language).subtract(`1`, `quarter`).startOf(`quarter`).toDate(),
                endDate: moment(new Date()).locale(language).subtract(`1`, `quarter`).endOf(`quarter`).toDate(),
              }),
              isSelected: (selectionRange: Range) => {
                const definedRange = {
                  startDate: moment(new Date()).locale(language).subtract(`1`, `quarter`).startOf(`quarter`).toDate(),
                  endDate: moment(new Date()).locale(language).subtract(`1`, `quarter`).endOf(`quarter`).toDate(),
                };
                if (!selectionRange.startDate || !selectionRange.endDate) {
                  return false;
                }

                return (
                  isSameDay(selectionRange.startDate, definedRange.startDate) &&
                  isSameDay(selectionRange.endDate, definedRange.endDate)
                );
              },
            },
            {
              label: intl.formatMessage({ id: `date_range_picker.this_year` }),
              range: (props?: DefinedRangeProps) => ({
                startDate: moment(new Date()).locale(language).startOf(`year`).toDate(),
                endDate: new Date(),
              }),
              isSelected: (selectionRange: Range) => {
                const definedRange = {
                  startDate: moment(new Date()).locale(language).startOf(`year`).toDate(),
                  endDate: new Date(),
                };
                if (!selectionRange.startDate || !selectionRange.endDate) {
                  return false;
                }

                return (
                  isSameDay(selectionRange.startDate, definedRange.startDate) &&
                  isSameDay(selectionRange.endDate, definedRange.endDate)
                );
              },
            },
            {
              label: intl.formatMessage({ id: `date_range_picker.last_year` }),
              range: (props?: DefinedRangeProps) => ({
                startDate: moment(new Date()).locale(language).subtract(`1`, `year`).startOf(`year`).toDate(),
                endDate: moment(new Date()).locale(language).subtract(`1`, `year`).endOf(`year`).toDate(),
              }),
              isSelected: (selectionRange: Range) => {
                const definedRange = {
                  startDate: moment(new Date()).locale(language).subtract(`1`, `year`).startOf(`year`).toDate(),
                  endDate: moment(new Date()).locale(language).subtract(`1`, `year`).endOf(`year`).toDate(),
                };
                if (!selectionRange.startDate || !selectionRange.endDate) {
                  return false;
                }

                return (
                  isSameDay(selectionRange.startDate, definedRange.startDate) &&
                  isSameDay(selectionRange.endDate, definedRange.endDate)
                );
              },
            },
          ]}
          onChange={(preview: RangeKeyDict) => {
            setRange(preview.range1);
          }}
        />
        <Grid container direction="column">
          <DateRange
            startDatePlaceholder={intl.formatMessage({ id: `date_range_picker.start_date_placeholder` })}
            endDatePlaceholder={intl.formatMessage({ id: `date_range_picker.end_date_placeholder` })}
            key={id}
            locale={(dateFnsLocales as Record<string, any>)[language]}
            maxDate={moment(new Date()).add(`3`, `year`).toDate()}
            minDate={moment(new Date()).subtract(`3`, `year`).toDate()}
            weekStartsOn={language === `en` ? 0 : 1}
            months={2}
            editableDateInputs
            direction="horizontal"
            ranges={[range]}
            rangeColors={[`#406867`]}
            onChange={(ranges: any) => {
              const range = ranges.range1;
              if (range.startDate && range.endDate) {
                range.endDate = new Date((range.endDate as Date).setHours(23, 59, 59, 999));
              }
              setRange(range);
            }}
            dateDisplayFormat={language === `en` ? `MM-dd-yyyy` : `dd-MM-yyyy`}
            navigatorRenderer={renderMonthAndYear}
          />
          <div className="rdrFooterActions">
            <Button variant="flat" intlId="cancel" onClick={onClose} />

            <HSpacer small />

            <Button
              intlId="confirm"
              onClick={() => {
                onUpdate(range);
                onClose();
              }}
            />
          </div>
        </Grid>
      </Grid>
    </Popover>
  );
};
